// src/LoginPage.tsx

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Container,
  keyframes,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { memo, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import Snowfall from "react-snowfall";

import { getEnvs } from "@/shared/utils/get-envs";
import { useLoaders } from "@/shared/hooks/useLoaders";
import { usernameLoginSchema } from "@/shared/utils/validationSchema";
import { useLogin } from "@/shared/actions/auth/auth.actions";
import { useFetchEmpresasFreeEndpoint } from "@/shared/actions/erp/administration/empresas.actions";
import CustomAutocomplete from "@/shared/components/ui/CustomAutocomplete";
import CustomPasswordTextField from "@/shared/components/ui/CustomPasswordTextField";
import CustomTextField from "@/shared/components/ui/CustomTextField";
import { EmpresaFreeEndpoint } from "@/shared/interfaces/erp/administration/empresas.interface";
import { useFreeLogin } from "@/store/auth/login.store";
// import { useQueryClient } from "@tanstack/react-query";
import { styles } from "./LoginPage.styles";
import Fireworks from "./Fireworks";
import NewYearAnimation from "./NewYearAnimation";
import DosFactorLoginDialog from "./DosFactorLoginDialog";

const {
  VITE_LOGO_EVA,
  VITE_FONDO_EVA
} = getEnvs()

/** -------------------- Tipo de fondo según fecha -------------------- **/
type BackgroundType = 'christmas' | 'fireworks' | 'none';

/**
 * Determina qué fondo mostrar según la fecha actual:
 * - christmas: Del 1 al 28 de diciembre (nieve + Santa)
 * - fireworks: Del 29 de diciembre al 6 de enero (fuegos artificiales)
 * - none: Resto del año (fondo normal)
 */
function getBackgroundType(): BackgroundType {
  const now = new Date();
  const month = now.getMonth(); // 0-11 (0=enero, 11=diciembre)
  const day = now.getDate();

  // Diciembre (mes 11)
  if (month === 11) {
    if (day >= 1 && day <= 28) {
      return 'christmas';
    }
    if (day >= 29) {
      return 'fireworks';
    }
  }

  // Enero (mes 0)
  if (month === 0 && day >= 1 && day <= 6) {
    return 'fireworks';
  }

  return 'none';
}

/** -------------------- Animaciones de Santa -------------------- **/
const santaMovAnimation = keyframes`
  0%, 100% {
    bottom: 0;
    left: 0;
    transform: translateX(-100%) rotate(-10deg);
  }
  30% {
    bottom: 100%;
    left: 100%;
    transform: translateX(100%) rotate(-10deg);
  }
  40% {
    bottom: 0;
    left: 100%;
    transform: translateX(100%) rotateY(180deg);
  }
  70% {
    bottom: 0;
    left: 0;
    transform: translateX(-100%) rotateY(180deg);
  }
`;

const handLeftAnimation = keyframes`
  0% { transform: rotate(15deg); }
  100% { transform: rotate(-30deg); }
`;

const handRightAnimation = keyframes`
  0% { transform: rotate(15deg); }
  100% { transform: rotate(-6deg); }
`;

const ropeLeftAnimation = keyframes`
  0% { transform: rotate(2deg); }
  100% { transform: rotate(5deg); }
`;

const ropeRightAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(-4deg); }
`;

const ropeInnerAnimation = keyframes`
  0% { transform: rotate(-15deg); }
  100% { transform: rotate(-15deg) translateY(3px); }
`;

const legFrontAnimation = keyframes`
  0% { transform: rotate(-24deg); }
  100% { transform: rotate(-13deg); }
`;

const legFrontExtAnimation = keyframes`
  0% { transform: rotate(131deg); }
  100% { transform: rotate(0); }
`;

const legBackAnimation = keyframes`
  0% { transform: rotate(-73deg); }
  100% { transform: rotate(-95deg); }
`;

const legBackExtAnimation = keyframes`
  0% { top: 4px; left: -4px; transform: rotate(50deg); }
  100% { top: 0; left: 0; transform: rotate(0); }
`;

/** -------------------- Componente Reno -------------------- **/
const Reindeer = memo(function Reindeer({ isSecond = false }: { isSecond?: boolean }) {
  return (
    <Box
      className="reindeer"
      sx={{
        position: "absolute",
        width: 115,
        height: 155,
        top: isSecond ? 0 : 50,
        right: 0,
        transform: isSecond ? "rotate(14deg)" : "rotate(14deg) translateX(calc(-100% - 30px))",
        zIndex: 0,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 65,
          left: 76,
          width: 8,
          height: 31,
          bgcolor: "#520d0d",
          zIndex: 10,
          transform: "rotate(-55deg)",
        },
      }}
    >
      {isSecond && (
        <Box
          className="rope"
          sx={{
            position: "absolute",
            top: 70,
            left: -55,
            width: 155,
            height: 33,
            overflow: "hidden",
            transform: "rotate(-15deg)",
            animation: `${ropeInnerAnimation} 0.3s linear alternate infinite`,
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: -12,
              width: "100%",
              height: 48,
              borderBottom: "1px solid #fff",
              borderRadius: "50%",
            },
          }}
        />
      )}
      {/* Face */}
      <Box
        className="face"
        sx={{
          position: "absolute",
          width: 30,
          height: 22,
          top: 44,
          left: 72,
          borderRadius: "10px 10px 50% 50%",
          transform: "rotate(-3deg)",
          bgcolor: "#cca58b",
          "&::before": {
            content: '""',
            position: "absolute",
            bgcolor: "#cca58b",
            width: 29,
            height: 16,
            borderRadius: "50%",
            top: 0,
            left: 11,
            transform: "rotate(-49deg)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bgcolor: "#000",
            width: 5,
            height: 5,
            borderRadius: "50%",
            top: -8,
            left: 31,
          },
        }}
      >
        <Box className="ear" sx={{
          position: "absolute", width: 21, height: 11, top: 4, left: -18,
          borderRadius: "4px 0 50% 50%", transform: "rotate(4deg)", bgcolor: "#cca58b",
          "&::before": {
            content: '""', position: "absolute", top: -2, left: 34, width: 4, height: 5,
            borderRadius: "50%", transform: "rotate(-35deg)", bgcolor: "#000",
          },
        }} />
        <Box className="horn right" sx={{
          position: "absolute", width: 24, height: 4, top: -12, left: -6,
          borderRadius: 2, transform: "rotate(62deg)", bgcolor: "#f0dbc2",
          "&::before": { content: '""', position: "absolute", bgcolor: "#f0dbc2", borderRadius: 2, top: -3, left: 5, width: 10, height: 4, transform: "rotate(43deg)" },
          "&::after": { content: '""', position: "absolute", bgcolor: "#f0dbc2", borderRadius: 2, top: -3, left: 11, width: 10, height: 4, transform: "rotate(53deg)" },
        }} />
        <Box className="horn left" sx={{
          position: "absolute", width: 29, height: 4, top: -7, left: -21,
          borderRadius: 2, transform: "rotate(38deg)", bgcolor: "#f0dbc2",
          "&::before": { content: '""', position: "absolute", bgcolor: "#f0dbc2", borderRadius: 2, top: -4, left: 6, width: 14, height: 4, transform: "rotate(43deg)" },
          "&::after": { content: '""', position: "absolute", bgcolor: "#f0dbc2", borderRadius: 2, top: -4, left: 13, width: 14, height: 4, transform: "rotate(53deg)" },
        }} />
      </Box>
      {/* Body */}
      <Box
        className="body"
        sx={{
          position: "absolute",
          width: 58,
          height: 31,
          top: 84,
          left: 28,
          borderRadius: "50% 0",
          transform: "rotate(-3deg)",
          bgcolor: "#cca58b",
          "&::before": {
            content: '""', position: "absolute", width: 46, height: 26, top: -15, left: 32,
            borderRadius: "0 0 50% 50%", transform: "rotate(-55deg)", bgcolor: "#cca58b",
          },
          "&::after": {
            content: '""', position: "absolute", width: 43, height: 26, top: -11, left: 29,
            borderRadius: "0 0 50% 50%", transform: "rotate(-30deg)", bgcolor: "#cca58b",
          },
        }}
      >
        <Box className="foot front">
          <Box className="inner" sx={{
            position: "absolute", width: 40, height: 8, top: 13, left: 35,
            borderRadius: "0 50%", transform: "rotate(-17deg)", bgcolor: "#cca58b",
            animation: `${legFrontAnimation} 0.3s linear alternate infinite`,
          }}>
            <Box className="extension" sx={{
              position: "absolute", width: 28, height: 8, top: 3, left: "100%",
              borderRadius: "2px 50%", transform: "rotate(131deg)", bgcolor: "#cca58b",
              transformOrigin: "top left",
              animation: `${legFrontExtAnimation} 0.2s linear alternate infinite`,
              "&::after": { content: '""', position: "absolute", top: 0, right: -2, width: 7, height: 8, borderRadius: 2, bgcolor: "#000" },
            }} />
          </Box>
        </Box>
        <Box className="foot back">
          <Box className="inner" sx={{
            position: "absolute", width: 56, height: 9, top: 37, left: -29,
            borderRadius: "0 50%", transform: "rotate(-73deg)",
            animation: `${legBackAnimation} 0.3s linear alternate infinite`,
            "&::before": { content: '""', position: "absolute", width: 25, height: 16, top: 4, left: 25, borderRadius: "0 50%", transform: "rotate(15deg)", bgcolor: "#cca58b" },
            "&::after": { content: '""', position: "absolute", right: 0, width: "65%", height: "100%", bgcolor: "#cca58b" },
          }}>
            <Box className="extension" sx={{
              position: "absolute", top: 0, left: 0, width: 28, height: 8,
              bgcolor: "#cca58b", borderRadius: "2px 50%", transformOrigin: "top right",
              animation: `${legBackExtAnimation} 0.2s linear alternate infinite`,
              "&::after": { content: '""', position: "absolute", width: 8, height: 9, top: -2, left: -2, borderRadius: "2px 0 2px 2px", transform: "rotate(14deg)", bgcolor: "#000" },
            }} />
          </Box>
        </Box>
        <Box className="tail" sx={{
          position: "absolute", width: 27, height: 26, top: 6, left: -8,
          borderRadius: "50% 2px", transform: "rotate(-17deg)", bgcolor: "#cca58b",
          "&::before": { content: '""', position: "absolute", bgcolor: "#cca58b", borderRadius: "50%", top: -2, left: -3, width: 15, height: 5, transform: "rotate(25deg)" },
        }} />
      </Box>
    </Box>
  );
});

/** -------------------- Componente Trineo de Santa -------------------- **/
const SantaSleigh = memo(function SantaSleigh() {
  return (
    <Box
      className="santaWrapper"
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        width: 430,
        height: 155,
        transform: "translateX(-100%) scale(0.7)",
        animation: `${santaMovAnimation} 20s linear infinite`,
        zIndex: 1,
        pointerEvents: "none",
      }}
    >
      {/* Santa */}
      <Box
        className="santa"
        sx={{
          position: "absolute",
          left: 0,
          bottom: 0,
          width: 125,
          height: 107,
          zIndex: 10,
          transform: "rotateY(180deg)",
          "&::before": {
            content: '""', position: "absolute", bottom: 0, left: -10, width: 126, height: 30,
            bgcolor: "#520d0d", borderRadius: "10px 50% 10px 50%", zIndex: 10,
          },
          "&::after": {
            content: '""', position: "absolute", bottom: 0, left: 70, width: 50, height: 53,
            bgcolor: "#520d0d", borderRadius: "50% 10px 22px 10px", transform: "rotate(8deg)",
          },
        }}
      >
        {/* Hat */}
        <Box className="hat">
          <Box sx={{
            position: "absolute", top: 7, left: 31, width: 43, height: 58,
            borderRadius: "50%", transform: "rotate(28deg)", bgcolor: "#d63527",
            "&::before": { content: '""', position: "absolute", top: 9, left: 45, width: 7, height: 7, borderRadius: "50%", bgcolor: "#fff" },
            "&::after": { content: '""', position: "absolute", top: 3, left: 19, width: 30, height: 7, borderRadius: "50%", transform: "rotate(22deg)", bgcolor: "#d63527" },
          }} />
          <Box sx={{
            position: "absolute", top: 18, left: 31, width: 44, height: 34,
            borderRadius: "50%", transform: "rotate(12deg)", bgcolor: "#fff",
          }} />
        </Box>
        {/* Face */}
        <Box className="face" sx={{
          position: "absolute", top: 25, left: 37, width: 31, height: 17,
          borderRadius: "20px 20px 50% 50%", transform: "rotate(10deg)", bgcolor: "#fde2b7", zIndex: 10,
        }}>
          <Box className="eyeBrows left" sx={{ position: "absolute", top: 2, left: 22, width: 2, height: 7, borderRadius: "50%", bgcolor: "#fff", transform: "rotate(-65deg)" }} />
          <Box className="eyeBrows right" sx={{ position: "absolute", top: 1, left: 4, width: 2, height: 7, borderRadius: "50%", bgcolor: "#fff", transform: "rotate(65deg)" }} />
          <Box className="eye left" sx={{ position: "absolute", top: 8, left: 2, width: 3, height: 4, borderRadius: "50%", bgcolor: "#000" }} />
          <Box className="eye right" sx={{ position: "absolute", top: 8, left: 20, width: 3, height: 4, borderRadius: "50%", bgcolor: "#000" }} />
          <Box className="nose" sx={{ position: "absolute", top: 10, left: 6, width: 12, height: 9, borderRadius: "50%", zIndex: 10, bgcolor: "#f7d194" }} />
          <Box className="cheek left" sx={{ position: "absolute", top: 12, left: -3, width: 7, height: 7, borderRadius: "50%", zIndex: 10, bgcolor: "#f4cfe3" }} />
          <Box className="cheek right" sx={{ position: "absolute", top: 13, left: 22, width: 7, height: 7, borderRadius: "50%", zIndex: 10, bgcolor: "#f4cfe3" }} />
          <Box className="beard">
            <Box sx={{
              position: "absolute", top: 8, left: -14, width: 15, height: 17, borderRadius: "50%", bgcolor: "#fff",
              "&::before": { content: '""', position: "absolute", top: 12, left: 1, width: 15, height: 17, borderRadius: "50%", bgcolor: "#fff" },
            }} />
            <Box sx={{
              position: "absolute", top: 16, left: -8, width: 26, height: 30, borderRadius: "50%", bgcolor: "#fff",
              "&::before": { content: '""', position: "absolute", top: 16, left: 13, width: 19, height: 17, borderRadius: "50%", bgcolor: "#fff" },
              "&::after": { content: '""', position: "absolute", top: 1, left: 13, width: 19, height: 17, borderRadius: "50%", bgcolor: "#fff" },
            }} />
            <Box sx={{
              position: "absolute", top: 16, left: 14, width: 27, height: 28, borderRadius: "50%", bgcolor: "#fff",
              "&::before": { content: '""', position: "absolute", top: -4, left: 13, width: 17, height: 17, borderRadius: "50%", bgcolor: "#fff" },
            }} />
          </Box>
        </Box>
        {/* Body */}
        <Box className="body" sx={{
          position: "absolute", top: 54, left: 16, width: 88, height: 53,
          "&::before": { content: '""', position: "absolute", top: -23, right: -10, width: 53, height: 51, borderRadius: "42% 50%", bgcolor: "#f3f0f0", zIndex: -1 },
        }}>
          <Box className="sweater" sx={{
            position: "absolute", top: -3, left: 10, width: 45, height: 39,
            borderRadius: "50% 50% 10% 10%", bgcolor: "#d63527", zIndex: 5,
            "&::before": { content: '""', position: "absolute", top: 28, left: 0, width: 45, height: 5, bgcolor: "#000", transform: "rotate(1deg)" },
            "&::after": { content: '""', position: "absolute", top: 27, left: 10, width: 7, height: 5, bgcolor: "#000", border: "1px solid #fff", borderRadius: 3, transform: "rotate(1deg)" },
          }} />
          <Box className="hand left" sx={{ position: "absolute", top: 5, left: 19, width: 33, height: 30, overflow: "hidden" }}>
            <Box className="inner" sx={{
              position: "absolute", top: 10, left: 8, width: 49, height: 7, zIndex: 100,
              borderRadius: 10, transform: "rotate(12deg)", bgcolor: "#d63527",
              animation: `${handRightAnimation} 0.3s linear alternate infinite`,
              "&::before": { content: '""', position: "absolute", width: 8, height: 7, top: -2, left: -6, bgcolor: "#000", borderRadius: "50%", transform: "rotate(25deg)" },
            }} />
          </Box>
          <Box className="hand right" sx={{
            position: "absolute", top: 4, left: 3, width: 11, height: 7,
            borderRadius: 10, bgcolor: "#d63527", transform: "rotate(25deg)",
            animation: `${handLeftAnimation} 0.3s linear alternate infinite`,
            "&::before": { content: '""', position: "absolute", width: 8, height: 7, top: -2, left: -6, bgcolor: "#000", borderRadius: "50%", transform: "rotate(10deg)" },
          }} />
        </Box>
      </Box>

      {/* Ropes */}
      <Box className="rope" sx={{
        position: "absolute", top: 92, left: 84, width: 182, height: 33,
        overflow: "hidden", zIndex: 10, transformOrigin: "bottom right",
        animation: `${ropeRightAnimation} 0.3s linear alternate infinite`,
        "&::after": { content: '""', position: "absolute", bottom: 0, left: -12, width: "100%", height: 48, borderBottom: "1px solid #fff", borderRadius: "50%" },
      }} />
      <Box className="rope back" sx={{
        position: "absolute", top: 85, left: 105, width: 149, height: 33,
        overflow: "hidden", zIndex: 0, transform: "rotate(4deg)", transformOrigin: "top left",
        animation: `${ropeLeftAnimation} 0.3s linear alternate infinite`,
        "&::after": { content: '""', position: "absolute", bottom: 0, left: -12, width: "100%", height: 48, borderBottom: "1px solid #fff", borderRadius: "50%" },
      }} />

      {/* Sled */}
      <Box className="sled" sx={{
        position: "absolute", bottom: -12, left: 0, width: 145, height: 11,
        transform: "rotate(-3deg)", borderBottom: "5px solid #690e00",
        borderRight: "5px solid #690e00", borderRadius: 10, zIndex: 10,
        "&::before": { content: '""', position: "absolute", top: 2, left: 34, width: 5, height: 9, bgcolor: "#690e00" },
        "&::after": { content: '""', position: "absolute", top: 3, left: 108, width: 5, height: 8, bgcolor: "#690e00" },
      }} />

      {/* Reindeers */}
      <Reindeer />
      <Reindeer isSecond />
    </Box>
  );
});

/** -------------------- Copos de nieve personalizados -------------------- **/
const useSnowflakeImages = () => {
  const [images, setImages] = useState<HTMLImageElement[]>([]);

  useEffect(() => {
    const snowflakeSvgs = [
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50"><path d="M25 0v50M0 25h50M7 7l36 36M43 7L7 43" stroke="white" stroke-width="2"/><circle cx="25" cy="25" r="3" fill="white"/></svg>',
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50"><path d="M25 5v40M5 25h40M12 12l26 26M38 12L12 38" stroke="white" stroke-width="1.5"/><circle cx="25" cy="25" r="2" fill="white"/></svg>',
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50"><circle cx="25" cy="25" r="8" fill="white"/></svg>',
    ];

    const loadedImages = snowflakeSvgs.map((svg) => {
      const img = new Image();
      img.src = "data:image/svg+xml," + encodeURIComponent(svg);
      return img;
    });

    setImages(loadedImages);
  }, []);

  return images;
};

/** -------------------- Background Image (para fondo normal y navideño) -------------------- **/
const BackgroundImage = memo(function BackgroundImage() {
  return (
    <Box sx={styles.backgroundRoot} aria-hidden>
      <Box
        component="img"
        alt="Fondo de la aplicación"
        src={VITE_FONDO_EVA}
        loading="lazy"
        decoding="async"
        sx={styles.backgroundImg}
      />
    </Box>
  );
});

/** -------------------- Fondo Navideño (Nieve + Santa) -------------------- **/
const ChristmasBackground = memo(function ChristmasBackground({ animationsReady }: { animationsReady: boolean }) {
  const snowflakeImages = useSnowflakeImages();

  return (
    <>
      {/* Overlay oscuro */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.35)",
          zIndex: 0,
        }}
      />

      {/* Trineo de Santa - aparece después del delay */}
      {animationsReady && <SantaSleigh />}

      {/* Efecto de nieve - aparece después del delay */}
      {animationsReady && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          <Snowfall
            snowflakeCount={100}
            radius={[5, 15]}
            speed={[0.5, 2]}
            wind={[-0.5, 1.5]}
            images={snowflakeImages.length > 0 ? snowflakeImages : undefined}
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
            }}
          />
        </Box>
      )}
    </>
  );
});

export interface LoginPageInterface { }

export type UsernameFormData = {
  username: string;
  password: string;
  nombre_comercial: string; // empresa id
};

const LoginPage: React.FC<LoginPageInterface> = () => {
  ///* global state
  const isBlocked = useFreeLogin((s) => s.isBlocked);

  // Determinar qué tipo de fondo mostrar según la fecha
  const backgroundType = useMemo(() => getBackgroundType(), []);

  // Estado para la animación de Año Nuevo (se muestra cada vez que se carga la página)
  const [showNewYearAnimation, setShowNewYearAnimation] = useState(
    backgroundType === 'fireworks'
  );

  const handleAnimationEnd = () => {
    setShowNewYearAnimation(false);
  };

  // Delay para animaciones (esperar a que cargue la página)
  const [animationsReady, setAnimationsReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setAnimationsReady(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  // const queryClient = useQueryClient();

  ///* fetch data
  const {
    data: empresasPagingRes,
    isLoading: isLoadingEmpresas,
  } = useFetchEmpresasFreeEndpoint({ page_size: 100 });

  ///* form
  const usernameForm = useForm<UsernameFormData>({
    resolver: yupResolver(usernameLoginSchema),
  });
  const {
    handleSubmit: usernameHandleSubmit,
    formState: { errors: usernameErrors },
  } = usernameForm;

  ///* mutations ----------------
  const loginMutation = useLogin();

  ///* handlers ----------------
  const onSubmit = (data: UsernameFormData) => {
    if (isBlocked) return;
    loginMutation.mutate(data);
  };

  ///* effects ----------------
  useEffect(() => {
    if (isLoadingEmpresas) return;

    const firstBusiness = empresasPagingRes?.data?.[0];
    if (!firstBusiness) return;

    usernameForm.setValue("nombre_comercial", firstBusiness.codigo ?? "");
  }, [isLoadingEmpresas]);

  // useEffect(() => {
  //   queryClient.invalidateQueries();
  // }, []);

  // No mostrar loader global si la animación de año nuevo está activa
  useLoaders(isLoadingEmpresas && !showNewYearAnimation);

  return (
    <Box sx={{
      position: "relative",
      minHeight: "100vh",
      bgcolor: backgroundType === 'fireworks' ? '#000' : "background.default"
    }}>
      {/* Fondo según el tipo */}
      {backgroundType === 'fireworks' ? (
        // Fuegos artificiales (29 dic - 6 ene)
        <Fireworks />
      ) : (
        // Fondo normal con imagen
        <BackgroundImage />
      )}

      {/* Efecto navideño (1-28 dic) */}
      {backgroundType === 'christmas' && (
        <ChristmasBackground animationsReady={animationsReady} />
      )}

      {/* Animación de Año Nuevo (se muestra una vez al cargar) */}
      {showNewYearAnimation && (
        <NewYearAnimation onAnimationEnd={handleAnimationEnd} />
      )}

      <Container
        maxWidth="sm"
        sx={styles.container}
      >
        <Paper
          elevation={3}
          sx={styles.paper}
        >
          <Stack alignItems="center" >
            <Box
              component="img"
              alt="LogoEva"
              loading="lazy"
              decoding="async"
              src={VITE_LOGO_EVA}
              sx={styles.logo}
            />

          </Stack>

          <Box sx={styles.formBox} >
            <Stack spacing={3} sx={styles.formStack}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Bienvenido, ingresa tus credenciales para continuar
              </Typography>

              <CustomTextField
                label="Usuario"
                name="username"
                defaultValue={usernameForm.getValues().username}
                control={usernameForm.control}
                error={usernameErrors?.username}
                helperText={usernameErrors?.username?.message}
                required={true}
                overrideAsPassword
                enableToSubmitWithEnter={true}
                handleSubmit={usernameHandleSubmit(onSubmit)}
              />
              <CustomPasswordTextField
                label="Contraseña"
                name="password"
                errors={usernameForm.formState.errors}
                form={usernameForm}
                enableToSubmitWithEnter={true}
                handleSubmit={usernameHandleSubmit(onSubmit)}
              />

              <CustomAutocomplete<EmpresaFreeEndpoint>
                label="Seleccione la empresa"
                name="nombre_comercial"
                options={empresasPagingRes?.data || []}
                valueKey="razon_social"
                actualValueKey="codigo"
                defaultValue={usernameForm.getValues().nombre_comercial}
                optionLabelForEdit={usernameForm.getValues().nombre_comercial}
                isLoadingData={isLoadingEmpresas}
                control={usernameForm.control}
                error={usernameForm.formState.errors.nombre_comercial}
                helperText={
                  usernameForm.formState.errors.nombre_comercial?.message
                }
                enableToSubmitWithEnter={true}
                handleSubmit={usernameHandleSubmit(onSubmit)}
              />
            </Stack>

            <Button
              fullWidth
              size="large"
              sx={styles.submitButton}
              type="submit"
              variant="contained"
              disabled={isBlocked}
              onClick={usernameHandleSubmit(onSubmit)}
              color="inherit"
            >
              Iniciar sesión
            </Button>
          </Box>
        </Paper>
      </Container>

      <DosFactorLoginDialog />
    </Box>
  );
};

export default LoginPage;
